import { useStore } from 'hooks/useRootStore';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useThrottleFn } from 'hooks/useThrottleFn';
import { useMutation } from 'react-query';
import { AppLoader } from 'components/Core/AppLoader/AppLoader';
import { IElasticSyncData, IElasticSyncResponse, IElasticSyncRequest, IResyncElasticData } from 'stores/elastic/types';
import styles from './resyncPopUp.module.scss';

const ELASTIC_INDEX_DELAY_MS = 2000;

export const ResyncPopUp = observer(function () {
  const {
    popUpStore: { closePopUp, callBack },
    elasticStore: { syncBrandProductsToElastic },
    brandsStore: { getBrandProductCounts },
    alertStore: { showAlert },
  } = useStore();

  const [isSuccess, setIsSuccess] = useState(false);
  const [syncData, setSyncData] = useState<IElasticSyncData | null>(null);
  const [hasError, setHasError] = useState(false);

  // Mutation for elastic sync
  const { mutate: elasticSyncMutate, isLoading } = useMutation<IElasticSyncResponse, Error, IElasticSyncRequest>(
    syncBrandProductsToElastic,
    {
      mutationKey: 'syncBrandProductsToElastic',
      onSuccess: async (data) => {
        setIsSuccess(true);
        if (data?.data) {
          setSyncData(data.data);
        }
        showAlert('Elastic sync completed successfully', 'success');
        await new Promise((resolve) => setTimeout(resolve, ELASTIC_INDEX_DELAY_MS));
        await getBrandProductCounts();
      },
      onError: () => {
        setHasError(true);
        showAlert('Elastic sync failed', 'error');
      },
    }
  );

  const handleStart = useThrottleFn(() => {
    const data = callBack?.cb() as IResyncElasticData | undefined;
    if (data?.brandId && data.numberStop !== undefined) {
      elasticSyncMutate({
        brandId: data.brandId,
      });
    }
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loaderContainer}>
          <p className={styles.title}>This may take a 2 minutes</p>
          <AppLoader />
        </div>
      );
    }

    if (isSuccess) {
      return (
        <>
          <div className={styles.successContent}>
            <p className={styles.successTitle}>Success</p>
            <p className={styles.successDesc}>Synchronization completed successfully</p>
            {syncData && (
              <div className={styles.syncResults}>
                <p>
                  <strong>Deleted:</strong> {syncData.deletedCount}
                </p>
                <p>
                  <strong>Imported:</strong> {syncData.fullImported}
                </p>
                <p>
                  <strong>Duration:</strong> {syncData.durationSeconds.toFixed(2)}s
                </p>
              </div>
            )}
          </div>
          <footer className={styles.footer}>
            <button type='button' className={styles.btnSubmit} onClick={closePopUp}>
              Close
            </button>
          </footer>
        </>
      );
    }

    if (hasError) {
      return (
        <>
          <div className={styles.successContent}>
            <p className={styles.successTitle}>Error</p>
            <p className={styles.successDesc}>Synchronization failed. Please try again.</p>
          </div>
          <footer className={styles.footer}>
            <button type='button' className={styles.btnSubmit} onClick={closePopUp}>
              Close
            </button>
          </footer>
        </>
      );
    }

    return (
      <>
        <p className={styles.title}>Elasticsearch Resync Confirmation</p>
        <p className={styles.desc}>Are you sure you want to start Elasticsearch synchronization?</p>
      </>
    );
  };

  return (
    <form className={styles.form}>
      {renderContent()}
      {!isSuccess && !hasError && (
        <footer className={styles.footer}>
          <button type='reset' className={styles.btnReset} onClick={closePopUp} disabled={isLoading}>
            Cancel
          </button>
          <button type='button' className={styles.btnSubmit} onClick={handleStart} disabled={isLoading}>
            Start
          </button>
        </footer>
      )}
    </form>
  );
});
